/**
 * Turn an artifact into a downloadable file descriptor. PURE (no DOM) so the
 * filename/mime/extension mapping is unit-testable; the actual Blob + anchor
 * download is a thin DOM wrapper (`downloadArtifact`) built on top.
 *
 * - A document artifact exports as Markdown (.md) — it already IS markdown.
 * - A code artifact exports with an extension derived from its fence language
 *   (python → .py, etc.), falling back to .txt for an unknown/absent language.
 */
import type { Artifact } from './detect';

export type ExportFile = {
  filename: string;
  mime: string;
  text: string;
};

/** Common fence languages → file extension. Unknown languages fall back to txt. */
const LANG_EXT: Record<string, string> = {
  python: 'py',
  py: 'py',
  javascript: 'js',
  js: 'js',
  typescript: 'ts',
  ts: 'ts',
  tsx: 'tsx',
  jsx: 'jsx',
  json: 'json',
  bash: 'sh',
  sh: 'sh',
  shell: 'sh',
  sql: 'sql',
  yaml: 'yaml',
  yml: 'yaml',
  html: 'html',
  css: 'css',
  markdown: 'md',
  md: 'md',
  text: 'txt',
  plaintext: 'txt',
};

/** Slugify a title into a safe, lowercase, hyphenated filename stem. */
function slugify(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  return slug || 'artifact';
}

/**
 * Build the file descriptor for an artifact: { filename, mime, text }. Pure —
 * given the same artifact it always yields the same descriptor.
 */
export function buildExportFile(artifact: Artifact): ExportFile {
  const stem = slugify(artifact.title);
  if (artifact.kind === 'document') {
    return {
      filename: `${stem}.md`,
      mime: 'text/markdown',
      text: artifact.content,
    };
  }
  const ext = (artifact.language && LANG_EXT[artifact.language]) || 'txt';
  const mime = ext === 'txt' ? 'text/plain' : 'text/plain;charset=utf-8';
  return {
    filename: `${stem}.${ext}`,
    mime,
    text: artifact.content,
  };
}

/**
 * Trigger a browser download of the artifact. DOM side-effect only — the
 * filename/content decisions live in `buildExportFile` (tested separately).
 * No-op-safe in non-DOM environments (guards on document existence).
 */
export function downloadArtifact(artifact: Artifact): void {
  if (typeof document === 'undefined') return;
  const { filename, mime, text } = buildExportFile(artifact);
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
